import { zipCodes } from "@/assets/data/zip_codes";
import { Button } from "@/components/Button";
import { ProgressBar } from "@/components/ProgressBar";
import { TextInput } from "@/components/TextInput";
import { useState, useMemo } from "react";
import FilledCheckboxIcon from "../../../../assets/icons/checkbox-filled.svg?react";
import { RegistrationFormData } from "../context/registration-context";
import { faCheckCircle, faUserPlus, faRocket, faArrowRight, faPlay, faUser, faClipboardList } from "@fortawesome/free-solid-svg-icons";
import { TopCallout } from "@/components/TopCallout";


export default function passwordValidation(password: string) {
  //   add special character
  const passwordRegex =
    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*-_]{8,}$/;
  return passwordRegex.test(password);
}

interface BasicInfoFormProps {
  onContinue: () => void;
  onBack?: () => void;
  isFromLogin?: boolean;
  partialSignUpInfo?: any;
  saving?: boolean;
  formData: RegistrationFormData;
  updateFormData: (data: Partial<RegistrationFormData>) => void;
}

export const BasicInfoForm: React.FC<BasicInfoFormProps> = ({
  onContinue,
  onBack,
  saving = false,
  formData,
  updateFormData
}) => {
  const [error, setError] = useState<string>("");
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());

  const isZipValid = (code: string) => {
    const isValid = code && (code.trim().length === 5 && !isNaN(parseInt(code, 10)) && !!zipCodes[code]);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDirtyFields(prev => new Set([...prev, name]));
    updateFormData({ [name]: value });
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setDirtyFields(prev => new Set([...prev, field]));
    updateFormData({ [field]: checked });
  };

  // Get all validation errors (frontend + backend)
  const getValidationErrors = (): string[] => {
    const validationErrors: string[] = [];
    
    // Frontend validation errors (only for dirty fields)
    if (dirtyFields.has('firstName') && !formData.firstName) {
      validationErrors.push("Client first name is required");
    }
    if (dirtyFields.has('lastName') && !formData.lastName) {
      validationErrors.push("Client last name is required");
    }
    if (dirtyFields.has('dob') && !formData.dob) {
      validationErrors.push("Client date of birth is required");
    }
    if (dirtyFields.has('zipCode')) {
      if (!formData.zipCode) {
        validationErrors.push("Please provide client's valid five digit zip code");
      } else if (!isZipValid(formData.zipCode)) {
        validationErrors.push("Please provide a valid zip code");
      }
    }
    if (dirtyFields.has('password')) {
      if (!formData.password) {
        validationErrors.push("Password is required");
      } else if (!passwordValidation(formData.password)) {
        validationErrors.push("Password should contain at least 8 characters, 1 letter, 1 number and 1 special character");
      }
    }
    if (dirtyFields.has('confirmPassword')) {
      if (!formData.confirmPassword) {
        validationErrors.push("Please confirm your password");
      } else if (formData.password && formData.password !== formData.confirmPassword) {
        validationErrors.push("Passwords do not match");
      }
    }
    
    // Backend errors (always show)
    if (error) {
      validationErrors.push(error);
    }
    
    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setError("");
      onContinue();
    } catch (error: any) {
      setError(error?.response?.data?.message || "Something went wrong");
    }
  };

  const disabled = useMemo(() => {
    return !formData.firstName ||
           !formData.lastName ||
           !formData.dob ||
           !formData.zipCode ||
           !isZipValid(formData.zipCode) ||
           !formData.password ||
           !passwordValidation(formData.password) ||
           !formData.confirmPassword ||
           formData.password !== formData.confirmPassword;
  }, [formData]);

  const minDate = useMemo(() => {
    // 100 years back
    const date = new Date();
    date.setFullYear(date.getFullYear() - 100);
    return date.toISOString().split("T")[0];
  }, []);

  const [zipCity, zipState] = useMemo(() => {
    return isZipValid(formData.zipCode) ? [zipCodes[formData.zipCode]?.city, zipCodes[formData.zipCode]?.state_short] : [undefined, undefined];
  }, [formData.zipCode]);

  return (
    <>
      <TopCallout
        icon={faUserPlus}
        title="Let's Get Started"
      >
        Fill out the form below to create your account.
      </TopCallout>
      <div className="bg-white p-8 rounded-[16px] shadow-lg">
        <ProgressBar currentStep={1} totalSteps={5} />

        <form className="flex flex-col gap-[22px]" onSubmit={handleSubmit}>
          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="flex-1 w-full flex flex-col gap-1.5">
              <label className="text-sm text-black/80">Client First Name</label>
              <TextInput
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                maxLength={128}
              />
            </div>
            <div className="flex-1 w-full flex flex-col gap-1.5">
              <label className="text-sm text-black/80">Client Last Name</label>
              <TextInput
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter last name"
                maxLength={128}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="flex-1 w-full flex flex-col gap-1.5">
              <label className="text-sm text-black/80">
                Client Date of Birth
              </label>
              <TextInput
                type="date"
                placeholder="mm/dd/yyyy"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                max={new Date().toISOString().split("T")[0]}
                min={minDate}
                className={`${!formData.dob ? "!text-[#8D8E92]" : ""} ${
                  zipCity ? "mb-6" : ""
                }`}
              />
            </div>
            <div className="flex-1 w-full flex flex-col gap-1.5">
              <label className="text-sm text-black/80">Service Zip Code</label>
              <TextInput
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                placeholder="Enter Zip Code"
              />
              {zipCity && zipState && (
                <div className="text-xs">
                  City: {zipCity}, {zipState}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 w-full flex flex-col gap-1.5">
            <label className="text-sm text-black/80">Password</label>
            <TextInput
              type="password"
              placeholder="Create a password"
              value={formData.password}
              onChange={handleChange}
              name="password"
            />
          </div>

          <div className="flex-1 w-full flex flex-col gap-1.5">
            <label className="text-sm text-black/80">Confirm Password</label>
            <TextInput
              type="password"
              placeholder="Re-enter the password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
          </div>

          {/* Communication Opt-in Checkbox */}
          <div className="flex items-center gap-1.5 mt-3">
            <div className="flex">
              <input
                type="checkbox"
                id="communicationOptIn"
                name="communicationOptIn"
                checked={formData.communicationOptIn}
                onChange={(e) =>
                  handleCheckboxChange("communicationOptIn", e.target.checked)
                }
                className={`w-[22px] h-[22px] cursor-pointer ${
                  formData.communicationOptIn ? "opacity-[0] !h-0 !w-0" : ""
                }`}
              />
              {formData.communicationOptIn ? (
                <button
                  onClick={() =>
                    handleCheckboxChange("communicationOptIn", false)
                  }
                >
                  <FilledCheckboxIcon className="w-[22px] h-[22px]" />
                </button>
              ) : null}
            </div>
            <label
              htmlFor="communicationOptIn"
              className="text-base text-[#1D1E1B]"
            >
              I opt in to communication and newsletters from Headstart Health
            </label>
          </div>

          <div className="">
            {/* Single error summary box */}
            {getValidationErrors().length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="text-red-800 text-sm font-medium mb-2">
                  Please fix the following errors:
                </div>
                <ul className="text-red-700 text-sm space-y-1">
                  {getValidationErrors().map((error, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">•</span>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3">
              {error && (
                <div className="text-red-500 text-xs font-medium mb-4 flex items-center gap-1">
                  {error}
                </div>
              )}
              <div className="flex gap-3">
                {onBack && (
                  <Button
                    type="button"
                    variant="gray"
                    className="!rounded-full flex-1"
                    onClick={onBack}
                    disabled={saving}
                  >
                    Back
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  className="!rounded-full flex-1"
                  disabled={disabled || saving}
                  loading={saving}
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
